/**
 * ActionAssuranceBadge — Universal trust signal for agent action governance.
 *
 * A single glanceable indicator answering "can I trust this action?"
 * Persona-aware: hidden in chat context for verified status, always shown
 * in audit/admin contexts.
 *
 * Motion: restrained. 200ms opacity fade on mount. No shimmer, no pulse
 * for stable states. State transitions use 300ms color crossfade.
 *
 * Blue Steel compliance:
 *   - Zero hardcoded colors (CSS variables only)
 *   - WCAG 2.1 AA: ARIA roles, keyboard navigation, focus ring
 *   - prefers-reduced-motion respected
 */

import type {
  AssuranceStatus,
  AssuranceContext,
  ActionAssuranceData,
} from './action-assurance-types';
import { ASSURANCE_COPY, assuranceStatusColor } from './action-assurance-types';

const React = (globalThis as any).React;
const { useState, useEffect, useRef, useCallback } = React;

export interface ActionAssuranceBadgeProps {
  status: AssuranceStatus;
  reason?: string;
  size?: 'sm' | 'md' | 'lg';
  context?: AssuranceContext;
  onClick?: () => void;
  className?: string;
}

const SIZE_PX = { sm: 20, md: 28, lg: 40 };
const ICON_SIZE = { sm: 12, md: 16, lg: 22 };

function ShieldIcon({ size, status }: { size: number; status: AssuranceStatus }) {
  const color = assuranceStatusColor(status);
  const checkColor = 'var(--color-bg-elevated, #fff)';

  return React.createElement('svg', {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': 'true',
  },
    React.createElement('path', {
      d: 'M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3z',
      fill: color,
      opacity: status === 'unavailable' ? 0.4 : 1,
    }),
    status === 'verified' && React.createElement('path', {
      d: 'M9.5 12.5l2 2 4-4',
      stroke: checkColor,
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      fill: 'none',
    }),
    status === 'needs_review' && React.createElement('text', {
      x: 12,
      y: 15,
      textAnchor: 'middle',
      fontSize: 10,
      fontWeight: 700,
      fill: checkColor,
    }, '!'),
    status === 'failed' && React.createElement('path', {
      d: 'M9.5 9.5l5 5M14.5 9.5l-5 5',
      stroke: checkColor,
      strokeWidth: 2,
      strokeLinecap: 'round',
      fill: 'none',
    }),
  );
}

export function ActionAssuranceBadge({
  status,
  reason,
  size = 'md',
  context = 'run_detail',
  onClick,
  className,
}: ActionAssuranceBadgeProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Context-dependent degradation: hide in chat context for verified/unavailable
  if (context === 'chat' && (status === 'verified' || status === 'unavailable')) {
    return null;
  }

  // Hide entirely if unavailable in non-audit contexts
  if (status === 'unavailable' && context !== 'review_task' && context !== 'investigation') {
    return null;
  }

  const copy = ASSURANCE_COPY[status];
  const tooltipText = reason || copy.tooltip;
  const sizePx = SIZE_PX[size];
  const iconSize = ICON_SIZE[size];

  const testId = `assurance-badge`;

  const containerStyle: Record<string, string | number> = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: size === 'lg' ? 8 : 4,
    padding: size === 'lg' ? '6px 12px' : size === 'md' ? '4px 8px' : '2px 4px',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: onClick ? 'pointer' : 'default',
    opacity: mounted ? 1 : 0,
    transition: 'opacity 200ms ease-out, background-color 300ms ease-out',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: size === 'lg' ? 14 : size === 'md' ? 12 : 11,
    color: assuranceStatusColor(status),
    lineHeight: 1,
  };

  const handleKeyDown = useCallback((e: any) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  }, [onClick]);

  const TagName = onClick ? 'button' : 'span';

  return React.createElement(TagName, {
    'data-testid': testId,
    className: className || undefined,
    style: containerStyle,
    title: tooltipText,
    'aria-label': `Action assurance: ${copy.label}. ${tooltipText}`,
    role: onClick ? 'button' : undefined,
    tabIndex: onClick ? 0 : undefined,
    onClick: onClick || undefined,
    onKeyDown: onClick ? handleKeyDown : undefined,
  },
    React.createElement(ShieldIcon, { size: iconSize, status }),
    size !== 'sm' && React.createElement('span', {
      style: { fontWeight: 500 },
    }, copy.label),
  );
}
